// Start screen: the habits of a day (today by default) to check off, the
// week strip to look back, and everything not planned for the day below.
import { ChevronDown, CloudOff, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { Habit } from '../../shared/api';
import type { DayKey } from '../../shared/days';
import { firstDay, isDue } from '../../shared/schedule';
import { Confetti } from '../components/Confetti';
import { EmptyState } from '../components/EmptyState';
import { HabitRow } from '../components/HabitRow';
import { ProgressRing } from '../components/ProgressRing';
import { SkeletonList } from '../components/Skeleton';
import { Sticker } from '../components/Sticker';
import { StickerBurst } from '../components/StickerBurst';
import { WeekStrip } from '../components/WeekStrip';
import { formatLongDay, greeting, relativeDay } from '../lib/format';
import { countOf, daysOf, setCount, sync, useStore, type DoneMap } from '../lib/store';
import { useToday } from '../lib/useToday';

function split(habits: readonly Habit[], done: DoneMap, day: DayKey) {
  const due: Habit[] = [];
  const free: Habit[] = [];
  for (const habit of habits) {
    const days = daysOf(done, habit.id);
    if (habit.archived || day < firstDay(habit, days)) continue;
    (isDue(habit, days, day) ? due : free).push(habit);
  }
  return { due, free };
}

function motivation(done: number, total: number, isToday: boolean): string {
  if (total === 0) return isToday ? 'Heute steht nichts an. Genieß den Tag! ☀️' : 'An diesem Tag stand nichts an.';
  if (done >= total) return isToday ? 'Alles erledigt – stark! 🎉' : 'Alles erledigt. 🎉';
  if (!isToday) return `${done} von ${total} erledigt.`;
  const left = total - done;
  if (done === 0) return total === 1 ? 'Ein Habit wartet auf dich.' : `${total} Habits warten auf dich.`;
  return left === 1 ? 'Nur noch eins – du schaffst das!' : `Noch ${left} – weiter so!`;
}

/** The platypus in the header, matching time of day and progress (today only). */
function moodSticker(done: number, total: number, hour: number): string {
  if (total === 0) return 'beach';
  if (done >= total) return hour >= 21 ? 'sleeping-teddy' : 'cheer';
  if (hour >= 18) return 'you-can-do-it';
  if (done > 0) return 'thumbs-up';
  return hour < 11 ? 'coffee' : 'wave';
}

export function TodayView() {
  const { habits, done, counts, loaded, error, offline, pending } = useStore();
  const today = useToday();
  const [picked, setPicked] = useState<DayKey | null>(null);
  const day = picked && picked <= today ? picked : today;
  const isToday = day === today;
  const [showFree, setShowFree] = useState(false);
  const [burst, setBurst] = useState(0);

  const { due, free } = useMemo(() => split(habits, done, day), [habits, done, day]);
  const doneCount = due.filter((h) => daysOf(done, h.id).has(day)).length;
  const allDone = due.length > 0 && doneCount === due.length;

  const progress = useMemo(() => {
    const cache = new Map<DayKey, { done: number; total: number }>();
    return (d: DayKey) => {
      let p = cache.get(d);
      if (!p) {
        const list = split(habits, done, d).due;
        p = { done: list.filter((h) => daysOf(done, h.id).has(d)).length, total: list.length };
        cache.set(d, p);
      }
      return p;
    };
  }, [habits, done]);

  const active = habits.filter((h) => !h.archived);
  const mood = moodSticker(doneCount, due.length, new Date().getHours());

  return (
    <div className="view">
      <header className="page-head">
        <div className="page-head__text">
          <p className="eyebrow">{isToday ? `${greeting()} · ${formatLongDay(day)}` : formatLongDay(day)}</p>
          <h1 className="page-title">{relativeDay(day, today)}</h1>
          {loaded && active.length > 0 && <p className="page-sub">{motivation(doneCount, due.length, isToday)}</p>}
        </div>
        {loaded && (due.length > 0 || (isToday && active.length > 0)) && (
          <div className={`page-head__side${due.length > 0 ? ' page-head__side--ring' : ''}`}>
            {isToday && <Sticker key={mood} id={mood} size={84} className="page-head__sticker" />}
            {due.length > 0 && (
              <ProgressRing
                ratio={doneCount / due.length}
                size={84}
                stroke={8}
                className={`page-head__ring${allDone ? ' ring--complete' : ''}`}
                label={`${doneCount} von ${due.length} erledigt`}
              >
                <span className="ring__count">
                  {doneCount}
                  <span className="ring__total">/{due.length}</span>
                </span>
              </ProgressRing>
            )}
          </div>
        )}
      </header>

      {offline && loaded && (
        <p className="status-pill" role="status">
          <CloudOff size={15} aria-hidden="true" />
          Offline
          {pending > 0 && ` · ${pending} ${pending === 1 ? 'Änderung' : 'Änderungen'} ausstehend`}
        </p>
      )}

      {!loaded && !error && <SkeletonList />}

      {!loaded && error && (
        <section className="notice glass">
          <p>{error}</p>
          <button type="button" className="btn btn--glass btn--sm" onClick={() => void sync()}>
            <RotateCcw size={16} /> Erneut versuchen
          </button>
        </section>
      )}

      {loaded && active.length === 0 && <EmptyState />}

      {loaded && active.length > 0 && (
        <>
          <WeekStrip selected={day} today={today} onSelect={(d) => setPicked(d === today ? null : d)} progress={progress} />

          {!isToday && (
            <button type="button" className="back-to-today" onClick={() => setPicked(null)}>
              Zurück zu heute
            </button>
          )}

          {due.length > 0 && (
            <ul className="habit-list" aria-label={isToday ? 'Heute geplant' : 'Geplant'}>
              {due.map((habit, i) => (
                <HabitRow
                  key={habit.id}
                  index={i}
                  habit={habit}
                  days={daysOf(done, habit.id)}
                  count={countOf(counts, habit.id, day)}
                  day={day}
                  today={today}
                  onToggle={(value) => {
                    setCount(habit.id, day, value);
                    // Confetti when the last open habit of the day gets checked (not on load).
                    const target = habit.schedule.kind === 'daily' ? habit.schedule.times ?? 1 : 1;
                    if (value >= target && doneCount + 1 === due.length) setBurst((b) => b + 1);
                  }}
                />
              ))}
            </ul>
          )}

          {free.length > 0 && (
            <section className="free">
              <button type="button" className="section-toggle" aria-expanded={showFree || due.length === 0} onClick={() => setShowFree((v) => !v)}>
                <span>{isToday ? 'Heute nicht geplant' : 'Nicht geplant'}</span>
                <span className="section-toggle__count">{free.length}</span>
                {due.length > 0 && <ChevronDown size={18} className="section-toggle__chevron" aria-hidden="true" />}
              </button>
              {(showFree || due.length === 0) && (
                <ul className="habit-list">
                  {free.map((habit, i) => (
                    <HabitRow
                      key={habit.id}
                      index={i}
                      habit={habit}
                      days={daysOf(done, habit.id)}
                      count={countOf(counts, habit.id, day)}
                      day={day}
                      today={today}
                      muted
                      onToggle={(value) => setCount(habit.id, day, value)}
                    />
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
      <Confetti burst={burst} />
      <StickerBurst burst={burst} />
    </div>
  );
}
